/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import {
  type SmartSearchChat,
  useSmartSearchChat,
} from './hooks/useSmartSearchChat';
import {
  conversationHistoryAtom,
  currentConversationIdAtom,
  pendingFirstMessageAtom,
} from './state';

const SmartSearchChatContext = createContext<SmartSearchChat | null>(null);

/** Returns the active chat, or `null` when no conversation is selected. */
export const useSmartSearchChatContext = (): SmartSearchChat | null =>
  useContext(SmartSearchChatContext);

/**
 * Variant that throws when no chat is active. Use inside components that
 * only render while a conversation is active (e.g. inside `Conversation`).
 */
export const useRequiredSmartSearchChatContext = (): SmartSearchChat => {
  const chat = useContext(SmartSearchChatContext);
  if (!chat) {
    throw new Error(
      'useRequiredSmartSearchChatContext must be used inside an active conversation',
    );
  }
  return chat;
};

/**
 * Provides a `useChat` instance scoped to the current conversation id.
 * Keyed on the id so switching conversations remounts the hook with fresh
 * initial messages and a clean stream.
 */
/**
 * Provides a `useChat` instance scoped to the current conversation id.
 * The inner element is keyed on the id so switching conversations remounts
 * the chat with fresh initial messages and a clean stream.
 */
export const SmartSearchChatProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const id = useAtomValue(currentConversationIdAtom);
  return (
    <ChatProvider key={id ?? 'none'} id={id}>
      {children}
    </ChatProvider>
  );
};

const ChatProvider: React.FC<{
  id: string | null;
  children: React.ReactNode;
}> = ({ id, children }) => {
  const history = useAtomValue(conversationHistoryAtom);
  const initialMessages = id
    ? (history.find(c => c.id === id)?.messages ?? [])
    : [];
  const chat = useSmartSearchChat({ id: id ?? '', initialMessages });

  const [pending, setPending] = useAtom(pendingFirstMessageAtom);
  const dispatchedRef = useRef(false);

  useEffect(() => {
    if (
      id &&
      pending &&
      !dispatchedRef.current &&
      chat.status === 'ready' &&
      chat.messages.length === 0
    ) {
      dispatchedRef.current = true;
      setPending(null);
      chat.sendMessage({ text: pending });
    }
  }, [
    id,
    pending,
    chat.status,
    chat.messages.length,
    chat.sendMessage,
    setPending,
    chat,
  ]);

  return (
    <SmartSearchChatContext.Provider value={id ? chat : null}>
      {children}
    </SmartSearchChatContext.Provider>
  );
};
